import { ButtonGroup, Icon, Text, useColors, mq } from "@certn/cascadia-react";
import { CascadiaColor, CascadiaStandardIcon } from "@certn/cascadia-tokens";
import { formatDate, parseDate } from "@certn/utils";
import { css } from "@emotion/css";
import React from "react";
import { Button } from "react-aria-components";
import { useTranslation } from "react-i18next";

type CascadiaBaseColorOptions =
  | "neutral"
  | "blue"
  | "green"
  | "purple"
  | "red"
  | "orange"
  | "tofino";

interface TaskListRowProps {
  icon: CascadiaStandardIcon;
  color: CascadiaBaseColorOptions;
  type: string;
  description: string;
  date: string | Date | null;
  actions: React.ReactNode;
  avatar?: React.ReactElement;
  onRowPress: () => void;
}

export function TaskListRow(props: TaskListRowProps) {
  const {
    icon = "emoji_objects",
    type,
    description,
    date,
    color = "purple",
    actions,
    avatar,
    onRowPress,
  } = props;
  const colors = useColors();
  const { t } = useTranslation();

  function numberOfDaysOrDate(date: Date | null): string {
    if (!date) return "--";

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    const timeDifference = Math.abs(date.getTime() - currentDate.getTime());
    const daysDifference = Math.ceil(timeDifference / (24 * 60 * 60 * 1000));
    if (daysDifference < 4) {
      switch (daysDifference) {
        case 0:
          return t("Today");
        case 1:
          return t("Yesterday");
        case 2:
        case 3:
          return t("{{count}} days ago", { count: daysDifference });
        default:
          return "--";
      }
    }
    return formatDate(date);
  }

  function iconTextColor(color: CascadiaBaseColorOptions): CascadiaColor {
    if (color === "neutral") return "icon-base";
    return `icon-accent-${color}-strong`;
  }

  return (
    <div className={styles.container}>
      <div>
        <div
          className={css([
            styles.icon,
            { backgroundColor: colors[`background-accent-${color}-weak`] },
          ])}
        >
          <Icon name={icon} color={iconTextColor(color)} size="space-6" />
        </div>
      </div>
      <div className={styles.textAndButtonContainer}>
        <Button
          className={styles.textContainer}
          onPress={onRowPress}
          data-testid="task-row-item-button"
        >
          <div>
            <Text style="global/label-small">{type}</Text>
            <Text style="global/info-label" className={styles.datePadding}>
              {numberOfDaysOrDate(parseDate(date))}
            </Text>
          </div>
          <Text
            className={styles.taskDescription}
            style="global/heading-medium"
          >
            {description}{" "}
          </Text>
        </Button>
        <ButtonGroup>
          {avatar && avatar} {actions}
        </ButtonGroup>
      </div>
    </div>
  );
}

const styles = {
  container: css({
    display: "flex",
    gap: "var(--space-3)",
    padding: "var(--space-3)",
  }),
  textAndButtonContainer: css(
    {
      display: "flex",
      flexGrow: 1,
      alignItems: "center",
      gap: "var(--space-3)",
    },
    mq({
      flexWrap: ["wrap", "nowrap"],
    })
  ),
  icon: css({
    borderRadius: "var(--border-radius-3)",
    display: "flex",
    padding: "var(--space-1-5)",
  }),
  textContainer: css({
    display: "flex",
    flexGrow: 1,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    flexBasis: "400px",
    width: "-webkit-fill-available",
    backgroundColor: "transparent",
    borderWidth: 0,
    paddingRight: 0,
    paddingLeft: 0,
    lineHeight: "var(--space-5)",
    cursor: "pointer",
  }),
  datePadding: css({
    paddingLeft: "var(--space-3)",
  }),
  taskDescription: css({
    textAlign: "left",
  }),
};
